import { useEffect, useState } from 'react';

export type ModifierKeysState = {
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
};

const defaultKeysState: ModifierKeysState = {
  alt: false,
  ctrl: false,
  meta: false,
};

// We track global keys state to use it in the hook initialization.
// It allows us to have proper initial modifiers keys state when component
// which relies on this hook is mounted after a modifier key is pressed.
let globalKeysState = defaultKeysState;

document.addEventListener('keydown', (event: KeyboardEvent): void => {
  globalKeysState = {
    alt: event.key === 'Alt' || globalKeysState.alt,
    ctrl: event.key === 'Control' || globalKeysState.ctrl,
    meta: event.key === 'Meta' || globalKeysState.meta,
  };
});

document.addEventListener('keyup', (event: KeyboardEvent): void => {
  globalKeysState = {
    alt: event.key === 'Alt' ? false : globalKeysState.alt,
    ctrl: event.key === 'Control' ? false : globalKeysState.ctrl,
    meta: event.key === 'Meta' ? false : globalKeysState.meta,
  };
});

window.addEventListener('blur', (): void => {
  globalKeysState = defaultKeysState;
});

export const useModifierKeys = (): ModifierKeysState => {
  const [modifierKeys, setModifierKeys] =
    useState<ModifierKeysState>(globalKeysState);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      setModifierKeys((prevKeys) => ({
        alt: event.key === 'Alt' || prevKeys.alt,
        ctrl: event.key === 'Control' || prevKeys.ctrl,
        meta: event.key === 'Meta' || prevKeys.meta,
      }));
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      setModifierKeys((prevKeys) => ({
        alt: event.key === 'Alt' ? false : prevKeys.alt,
        ctrl: event.key === 'Control' ? false : prevKeys.ctrl,
        meta: event.key === 'Meta' ? false : prevKeys.meta,
      }));
    };

    const handleBlur = (): void => {
      setModifierKeys(defaultKeysState);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return modifierKeys;
};
